<template>
  <div class="qr-scanner-wrapper">
    <div class="qr-scanner-container">
      <div id="qr-reader" ref="qrReader"></div>
      
      <!-- Overlay for QR scanning -->
      <div class="qr-overlay" v-if="isScanning">
        <div class="qr-corner tl"></div>
        <div class="qr-corner tr"></div>
        <div class="qr-corner bl"></div>
        <div class="qr-corner br"></div>
        <div class="qr-hint">
          <span>🔲 Align QR code within frame</span>
        </div>
      </div>
      
      <div v-if="!isScanning && !qrResult" class="qr-placeholder">
        <i class="material-icons" style="font-size: 48px; color: #666;">qr_code_scanner</i>
        <p style="color: #666; margin-top: 10px;">Click "Scan QR" to start</p>
      </div>
    </div>

    <!-- QR Controls -->
    <div class="qr-controls">
      <button 
        class="v-btn v-btn--small primary" 
        @click="startScanner" 
        :disabled="isScanning || isVerifying"
      >
        <i class="material-icons" style="font-size: 18px;">qr_code_scanner</i> Scan QR
      </button>
      <button 
        class="v-btn v-btn--small error" 
        @click="stopScanner" 
        v-if="isScanning"
      >
        <i class="material-icons" style="font-size: 18px;">stop</i> Stop
      </button>
      <button 
        class="v-btn v-btn--small warning" 
        @click="clearResult" 
        v-if="qrResult"
      >
        <i class="material-icons" style="font-size: 18px;">refresh</i> Clear
      </button>
    </div>

    <!-- QR Result -->
    <div v-if="qrResult" class="qr-result">
      <div v-if="isVerifying" class="verifying">
        <div class="spinner"></div>
        <span style="color: #aaa; margin-left: 10px;">Verifying with eCitizen...</span>
        <span style="color: #666; margin-left: 10px; font-size: 12px;">({{ scanAttempts }}/{{ maxScanAttempts }})</span>
      </div>
      
      <div v-else-if="qrVerificationResult" 
           class="verification-result" 
           :class="qrVerificationResult.verified ? 'success' : 'error'">
        
        <div v-if="qrVerificationResult.verified">
          <strong> {{ qrVerificationResult.document_type || 'Document' }} Verified</strong>
          <div class="qr-result-details">
            <span> {{ qrVerificationResult.holder_name || 'N/A' }}</span>
            <span v-if="qrVerificationResult.id_number"> {{ qrVerificationResult.id_number }}</span>
            <span> Expires: {{ qrVerificationResult.expiry_date || 'N/A' }}</span>
            <span class="status-badge" :class="qrVerificationResult.status">
              {{ qrVerificationResult.status || 'active' }}
            </span>
          </div>
        </div>
        
        <div v-else>
          <strong> Verification Failed</strong>
          <div class="result-message">{{ qrVerificationResult.error }}</div>
          <div v-if="scanAttempts < maxScanAttempts" class="retry-hint">
            <small>Attempt {{ scanAttempts }} of {{ maxScanAttempts }}. Retrying automatically...</small>
          </div>
          <div v-else class="retry-hint">
            <small style="color: #ff6b6b;"> Maximum attempts reached. Click "Clear" to try again.</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QRScanner',

props: {
  guest: { type: Object, default: null },
  guestKey: { type: Number, default: null },
  bookingId: { type: String, default: null }
},

  data() {
    return {
      html5QrCode: null,
      isScanning: false,
      isVerifying: false,
      qrResult: null,
      qrVerificationResult: null,
      scanAttempts: 0,
      maxScanAttempts: 3,
      retryTimer: null
    };
  },

  beforeDestroy() {
    this.clearRetryTimer();
    this.stopScanner();
  },

  methods: {
    startScanner() {
      this.clearRetryTimer();
      this.stopScanner();
      this.qrResult = null;
      this.qrVerificationResult = null;
      this.scanAttempts = 0;
      
      import('html5-qrcode')
        .then((module) => {
          const Html5Qrcode = module.Html5Qrcode;
          this.initScanner(Html5Qrcode);
        })
        .catch(() => {
          this.$emit('error', 'Failed to load QR scanner library. Please check internet connection.');
        });
    },

    initScanner(Html5Qrcode) {
      const readerElement = document.getElementById('qr-reader');
      if (!readerElement) {
        this.$emit('error', 'QR reader element not found');
        return;
      }

      this.html5QrCode = new Html5Qrcode('qr-reader');

      // HIGHER QUALITY CONFIGURATION
      const config = {
        fps: 15,
        qrbox: { width: 200, height: 200 },
        aspectRatio: 1.0,
        // Higher resolution for better QR detection
        videoConstraints: {
          facingMode: { ideal: "environment" },
          width: { ideal: 3840, max: 4096 },
          height: { ideal: 2160, max: 2160 },
          frameRate: { ideal: 30 }
        }
      };

      this.isScanning = true;
      this.$emit('scanning', true);

      // Try with high resolution first
      this.html5QrCode.start(
        { 
          facingMode: "environment",
          width: { ideal: 3840, max: 4096 },
          height: { ideal: 2160, max: 2160 },
          frameRate: { ideal: 30 }
        },
        config,
        (decodedText) => {
          this.onQrCodeDetected(decodedText);
        },
        () => {}
      ).catch((err) => {
        // If high resolution fails, fallback to standard resolution
        console.warn('High resolution failed, falling back to standard:', err);
        
        const fallbackConfig = {
          fps: 15,
          qrbox: { width: 200, height: 200 },
          aspectRatio: 1.0
        };

        this.html5QrCode.start(
          { facingMode: 'environment' },
          fallbackConfig,
          (decodedText) => {
            this.onQrCodeDetected(decodedText);
          },
          () => {}
        ).catch((fallbackErr) => {
          this.isScanning = false;
          this.$emit('scanning', false);
          
          let errorMsg = 'Failed to start QR scanner';
          if (fallbackErr.message && fallbackErr.message.includes('Permission denied')) {
            errorMsg = 'Camera permission denied. Please allow camera access in your browser settings.';
          } else if (fallbackErr.message && fallbackErr.message.includes('NotFoundError')) {
            errorMsg = 'No camera found on this device.';
          } else if (fallbackErr.message) {
            errorMsg = 'Failed to start QR scanner: ' + fallbackErr.message;
          }
          
          this.$emit('error', errorMsg);
        });
      });
    },

    stopScanner() {
      if (this.html5QrCode) {
        try {
          this.html5QrCode.stop().catch(() => {});
        } catch (e) {}
        this.html5QrCode = null;
      }
      this.isScanning = false;
      this.$emit('scanning', false);
    },

    onQrCodeDetected(decodedText) {
      this.stopScanner();
      
      this.qrResult = decodedText;
      this.isVerifying = true;
      
      if (decodedText.includes('dis.ecitizen.go.ke/api/')) {
        this.verifyWithEcizenApi(decodedText);
      } else {
        this.qrVerificationResult = {
          verified: false,
          error: 'Invalid QR code. Not an eCitizen verification URL.'
        };
        this.isVerifying = false;
        this.$emit('error', this.qrVerificationResult.error);
      }
    },

    verifyWithEcizenApi(apiUrl) {
      this.scanAttempts++;
      this.isVerifying = true;
      
      const sessionId = localStorage.getItem('lkjhgfdsa');
      
      this.$store.dispatch('verifyDocumentViaQrCode', {
        qr_url: apiUrl,
        guest_key: this.guestKey,
        guest_name: this.guest ? this.guest.payment_person : null,  
        booking_id: this.bookingId,
        session_id: sessionId
      })
      .then((result) => {
        this.isVerifying = false;
        
        const isVerified = result.verified || result.success || false;
        
        if (isVerified) {
          this.qrVerificationResult = {
            verified: true,
            document_type: result.document_type || 'Document',
            holder_name: result.holder_name || 'Unknown',
            id_number: result.id_number || null,
            expiry_date: result.expiry_date || 'N/A',
            status: result.status || 'active',
            source: 'ecitizen_api'
          };
          
          this.$emit('verified', this.qrVerificationResult);
        } else {
          const errorMsg = result.error || 'Document verification failed';
          this.qrVerificationResult = {
            verified: false,
            error: errorMsg
          };
          
          this.$emit('error', errorMsg);
        }
      })
      .catch((error) => {
        this.isVerifying = false;
        
        let errorMsg = 'Failed to verify with eCitizen API';
        if (error) {
          if (error.error) {
            errorMsg = error.error;
          } else if (error.message) {
            errorMsg = error.message;
          }
        }
        
        this.qrVerificationResult = {
          verified: false,
          error: errorMsg
        };
        
        this.$emit('error', errorMsg);
        
        if (this.scanAttempts < this.maxScanAttempts) {
          this.retryTimer = setTimeout(() => {
            this.startScanner();
          }, 3000);
        }
      });
    },

    clearRetryTimer() {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
    },

    clearResult() {
      this.clearRetryTimer();
      this.qrResult = null;
      this.qrVerificationResult = null;
      this.isVerifying = false;
      this.scanAttempts = 0;
    }
  }
};
</script>

<style scoped>
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #1976d2;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.qr-scanner-wrapper {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.qr-scanner-container {
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
}

#qr-reader {
  width: 100%;
  height: 100%;
}

#qr-reader video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}

.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
}

.qr-corner {
  position: absolute;
  width: 30px;
  height: 30px;
  border-color: #00ff64;
  border-style: solid;
  border-width: 0;
  transition: border-color 0.3s;
}

.qr-corner.tl {
  top: 10px;
  left: 10px;
  border-top-width: 4px;
  border-left-width: 4px;
}

.qr-corner.tr {
  top: 10px;
  right: 10px;
  border-top-width: 4px;
  border-right-width: 4px;
}

.qr-corner.bl {
  bottom: 10px;
  left: 10px;
  border-bottom-width: 4px;
  border-left-width: 4px;
}

.qr-corner.br {
  bottom: 10px;
  right: 10px;
  border-bottom-width: 4px;
  border-right-width: 4px;
}

.qr-hint {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  background: rgba(0, 0, 0, 0.7);
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
}

.qr-placeholder {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #555;
  z-index: 1;
}

.qr-controls {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 10px;
  flex-wrap: wrap;
}

.qr-controls .v-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

.qr-controls .v-btn.primary {
  background: #1976d2;
  color: white;
}

.qr-controls .v-btn.primary:hover {
  background: #1565c0;
}

.qr-controls .v-btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qr-controls .v-btn.error {
  background: #d32f2f;
  color: white;
}

.qr-controls .v-btn.error:hover {
  background: #c62828;
}

.qr-controls .v-btn.warning {
  background: #f57c00;
  color: white;
}

.qr-controls .v-btn.warning:hover {
  background: #e65100;
}

.qr-result {
  margin-top: 10px;
  padding: 10px;
  border-radius: 6px;
  background: #1a1a2e;
}

.verifying {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: #aaa;
}

.verification-result {
  padding: 10px;
  border-radius: 6px;
  text-align: center;
  font-weight: bold;
  font-size: 13px;
}

.verification-result.success {
  background: #1a3a1a;
  color: #4caf50;
  border: 1px solid #4caf50;
}

.verification-result.error {
  background: #3a1a1a;
  color: #ffffff;
  border: 1px solid #f44336;
}

.qr-result-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 6px;
  font-weight: normal;
  font-size: 12px;
  color: #ccc;
}

.result-message {
  margin-top: 4px;
  font-weight: normal;
  font-size: 12px;
}

.retry-hint {
  margin-top: 5px;
  color: #aaa;
  font-size: 12px;
}

.status-badge {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 10px;
  text-transform: uppercase;
}

.status-badge.active,
.status-badge.valid {
  background: #2e7d32;
  color: #a5d6a7;
}

.status-badge.expired,
.status-badge.inactive {
  background: #c62828;
  color: #ffcdd2;
}

@media (max-width: 600px) {
  .qr-hint {
    font-size: 11px;
    padding: 4px 12px;
    white-space: normal;
  }
  
  .qr-result-details {
    font-size: 11px;
    gap: 4px;
  }
}
</style>